import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ResponsiveOperationalTable from "./ResponsiveOperationalTable";
import ContextualHelp from "./ContextualHelp";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import { affiliatesApi } from "../../api/affiliates";

const BRAND = {
  orange: "#F35A3A",
  dark: "#19242F",
  page: "#F6F7F9",
  border: "rgba(25,36,47,.10)",
  muted: "rgba(25,36,47,.62)",
};

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
}

function money(value, currency = "ARS") {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency,
    maximumFractionDigits: currency === "ARS" ? 0 : 2,
  }).format(Number(value || 0));
}

function moneyList(values = {}) {
  const entries = Object.entries(values).filter(([, value]) => Number(value || 0) > 0);
  if (!entries.length) return money(0, "ARS");
  return entries.map(([currency, amount]) => money(amount, currency)).join(" + ");
}

function percent(value) {
  return new Intl.NumberFormat("es-ES", {
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function dateLabel(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat("es-ES", { dateStyle: "medium" }).format(parsed);
}

function billingStatus(status) {
  const map = {
    trial: { label: "Prueba", color: "warning" },
    pending: { label: "Pendiente", color: "warning" },
    active: { label: "Activa", color: "success" },
    past_due: { label: "Pago atrasado", color: "error" },
    paused: { label: "Pausada", color: "error" },
    cancelled: { label: "Cancelada", color: "default" },
    exempt: { label: "Bonificada", color: "success" },
  };
  return map[status] || { label: status || "Sin estado", color: "default" };
}

function commissionStatus(row, programEnabled) {
  if (row.commission?.eligible) {
    return { label: "Comisión válida", color: "success" };
  }
  if (!programEnabled) {
    return { label: "Programa pausado", color: "warning" };
  }
  if (String(row.billing?.status || "").toLowerCase() !== "active") {
    return { label: "Requiere mensualidad activa", color: "default" };
  }
  if (Number(row.billing?.monthlyAmount || 0) <= 0) {
    return { label: "Sin mensualidad comisionable", color: "default" };
  }
  if (Number(row.commission?.percentage || 0) <= 0) {
    return { label: "Sin participación vigente", color: "default" };
  }
  return { label: "No genera comisión", color: "default" };
}

function Metric({ icon, label, value, helper }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.35, sm: 1.75 },
        height: "100%",
        minWidth: 0,
        borderRadius: 3,
        border: `1px solid ${BRAND.border}`,
      }}
    >
      <Stack direction="row" spacing={{ xs: 0, sm: 1.1 }} alignItems="flex-start">
        <Avatar
          variant="rounded"
          sx={{
            display: { xs: "none", sm: "flex" },
            width: 36,
            height: 36,
            bgcolor: "rgba(243,90,58,.10)",
            color: BRAND.orange,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{ display: "block", fontWeight: 900, color: BRAND.muted, lineHeight: 1.2 }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              mt: 0.25,
              fontWeight: 950,
              color: BRAND.dark,
              overflowWrap: "anywhere",
              fontSize: { xs: ".9rem", sm: "1rem" },
              lineHeight: 1.25,
            }}
          >
            {value}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: { xs: "none", sm: "block" },
              mt: 0.3,
              color: BRAND.muted,
              lineHeight: 1.3,
            }}
          >
            {helper}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function AffiliateSettingsCard() {
  const [affiliate, setAffiliate] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    let active = true;

    affiliatesApi.getSummary()
      .then((data) => {
        if (active) setAffiliate(data?.affiliate || null);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || "No se pudo cargar tu red de referidos.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const markCopied = () => {
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const handleCopy = async () => {
    if (!affiliate?.link) return;

    try {
      await copyText(affiliate.link);
      markCopied();
    } catch (_error) {
      setError("No se pudo copiar automáticamente. Seleccioná el enlace y copialo manualmente.");
    }
  };

  const handleShare = async () => {
    if (!affiliate?.link) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "WebProfit",
          text: "Te comparto mi enlace de WebProfit.",
          url: affiliate.link,
        });
        return;
      } catch (shareError) {
        if (shareError?.name === "AbortError") return;
      }
    }

    try {
      await copyText(affiliate.link);
      markCopied();
    } catch (_error) {
      setError("No se pudo compartir automáticamente. Seleccioná el enlace y copialo manualmente.");
    }
  };

  const metrics = affiliate?.metrics || {};
  const projects = affiliate?.projects || [];
  const program = affiliate?.program || {};
  const policy = program.currentPolicy || {};

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: { xs: 3, md: 4 },
        border: `1px solid ${BRAND.border}`,
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: { xs: 1.75, sm: 2.25, md: 3 },
          color: "#fff",
          background: "linear-gradient(135deg, #19242F 0%, #2D3B48 66%, #F35A3A 145%)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={{ xs: 1.4, md: 2 }}
        >
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: "rgba(255,255,255,.12)",
                color: "#fff",
                width: { xs: 40, sm: 46 },
                height: { xs: 40, sm: 46 },
              }}
            >
              <ShareRoundedIcon />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 950, lineHeight: 1.2 }}>
                Tu red de referidos
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,.72)",
                  mt: 0.2,
                  display: { xs: "none", sm: "block" },
                }}
              >
                Compartí tu enlace y seguí proyectos y comisiones desde un solo lugar.
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.75} useFlexGap sx={{ minWidth: 0 }}>
            <Chip
              icon={<GroupsRoundedIcon />}
              label={`${metrics.directReferrals || 0} directos`}
              sx={{ flex: { xs: 1, sm: "0 0 auto" }, bgcolor: "rgba(255,255,255,.10)", color: "#fff", fontWeight: 900 }}
            />
            <Chip
              icon={<AccountTreeRoundedIcon />}
              label={`${metrics.networkReferrals || 0} en la red`}
              sx={{ flex: { xs: 1, sm: "0 0 auto" }, bgcolor: "rgba(255,255,255,.10)", color: "#fff", fontWeight: 900 }}
            />
          </Stack>
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mt: { xs: 1.5, md: 2.1 } }}>
          <TextField
            fullWidth
            size="small"
            value={affiliate?.link || ""}
            placeholder={loading ? "Generando tu enlace..." : "Enlace no disponible"}
            InputProps={{ readOnly: true }}
            inputProps={{ "aria-label": "Enlace personal de afiliado" }}
            sx={{
              minWidth: 0,
              "& .MuiOutlinedInput-root": { bgcolor: "#fff", minHeight: 44 },
              "& .MuiInputBase-input": { textOverflow: "ellipsis" },
            }}
          />
          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              flex: "0 0 auto",
              "& .MuiButton-root": {
                flex: { xs: 1, md: "0 0 auto" },
                minHeight: 44,
                borderRadius: 2.5,
                textTransform: "none",
                fontWeight: 900,
                whiteSpace: "nowrap",
              },
            }}
          >
            <Button
              variant="contained"
              onClick={handleCopy}
              disabled={loading || !affiliate?.link}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ContentCopyRoundedIcon />}
              sx={{
                minWidth: { md: 138 },
                bgcolor: BRAND.orange,
                "&:hover": { bgcolor: "#DF4728" },
              }}
            >
              {copied ? "Copiado" : "Copiar"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleShare}
              disabled={loading || !affiliate?.link}
              startIcon={<IosShareRoundedIcon />}
              sx={{
                minWidth: { md: 138 },
                color: "#fff",
                borderColor: "rgba(255,255,255,.42)",
                "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,.08)" },
                "&.Mui-disabled": { color: "rgba(255,255,255,.45)", borderColor: "rgba(255,255,255,.2)" },
              }}
            >
              Compartir
            </Button>
          </Stack>
        </Stack>

        {affiliate?.code ? (
          <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "rgba(255,255,255,.68)" }}>
            Código: <strong>{affiliate.code}</strong>
          </Typography>
        ) : null}
      </Box>

      <CardContent sx={{ p: { xs: 1.5, sm: 2.25, md: 3 }, "&:last-child": { pb: { xs: 1.5, sm: 2.25, md: 3 } } }}>
        <Stack spacing={{ xs: 1.75, md: 2.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          {!program.enabled && !loading ? (
            <Alert severity="warning">
              El programa está pausado. Tu red se conserva, pero no se generan nuevas comisiones.
            </Alert>
          ) : (
            <Paper
              elevation={0}
              sx={{
                px: { xs: 1.35, sm: 1.75 },
                py: 1.2,
                borderRadius: 3,
                bgcolor: "rgba(2,136,209,.055)",
                border: "1px solid rgba(2,136,209,.14)",
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography sx={{ minWidth: 0, color: BRAND.dark, fontSize: { xs: ".82rem", sm: ".9rem" }, fontWeight: 800 }}>
                  Comisión estimada: {percent(program.directCommissionPercent)}% directos · {percent(program.networkCommissionPercent)}% red
                </Typography>
                <ContextualHelp
                  compact
                  title="Cuándo una comisión es válida"
                  text="La estimación y lo efectivamente registrado son datos distintos."
                  bullets={[
                    "El programa debe estar activo y tu participación vigente debe ser mayor que 0%.",
                    "El proyecto debe tener una mensualidad activa y un importe mayor que 0.",
                    "La estimación usa la política actual; una comisión se registra solamente a partir de pagos confirmados.",
                    "Si un proyecto deja de cumplir las condiciones, no suma nueva estimación aunque conserve comisiones históricas ya registradas.",
                  ]}
                />
              </Stack>
            </Paper>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
              gap: { xs: 0.9, sm: 1.25 },
            }}
          >
            <Metric
              icon={<StorefrontRoundedIcon />}
              label="PROYECTOS"
              value={metrics.affiliatedProjects || 0}
              helper={`${metrics.activeAffiliatedProjects || 0} generan estimación`}
            />
            <Metric
              icon={<PaidRoundedIcon />}
              label="VOLUMEN MENSUAL"
              value={moneyList(metrics.monthlyVolumeByCurrency)}
              helper="Sólo mensualidades comisionables"
            />
            <Metric
              icon={<AccountTreeRoundedIcon />}
              label="ESTIMADA"
              value={moneyList(metrics.estimatedCommissionByCurrency)}
              helper="Proyección con la política actual"
            />
            <Metric
              icon={<PaidRoundedIcon />}
              label="CONFIRMADA"
              value={moneyList(metrics.availableCommissionByCurrency)}
              helper={`Pagado: ${moneyList(metrics.paidCommissionByCurrency)}`}
            />
          </Box>

          <Paper
            elevation={0}
            sx={{ px: { xs: 1.35, sm: 2 }, py: 1.35, borderRadius: 3, bgcolor: BRAND.page, border: `1px solid ${BRAND.border}` }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1.25,
                alignItems: "center",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.35} alignItems="center">
                  <Typography variant="caption" sx={{ fontWeight: 900, color: BRAND.muted }}>
                    POLÍTICA
                  </Typography>
                  <ContextualHelp
                    compact
                    title="Cómo se reparte la política"
                    text="La política vigente define cuánto del valor comisionable se asigna a la red y cómo se reduce la participación a medida que aumenta la profundidad."
                    bullets={[
                      `Red: ${percent(policy.affiliatePoolPercent)}% · WebProfit: ${percent(policy.webProfitPercent)}%.`,
                      "Tu porcentaje visible ya incorpora tu posición dentro de la red.",
                    ]}
                    buttonSx={{ bgcolor: "transparent", px: 0.45 }}
                  />
                </Stack>
                <Typography sx={{ mt: 0.15, fontWeight: 950, color: BRAND.dark, fontSize: { xs: ".85rem", sm: "1rem" } }}>
                  Red {percent(policy.affiliatePoolPercent)}%
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0, textAlign: "right" }}>
                <Typography variant="caption" sx={{ fontWeight: 900, color: BRAND.muted }}>
                  PROFUNDIDAD
                </Typography>
                <Typography sx={{ mt: 0.15, fontWeight: 950, color: BRAND.dark, fontSize: { xs: ".85rem", sm: "1rem" } }}>
                  {metrics.networkLevels || 0} niveles
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Divider />

          <Box>
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>
                Proyectos de tu red
              </Typography>
              <ContextualHelp
                compact
                title="Qué muestra cada proyecto"
                text="Cada registro conserva relación, módulos, estado de cobro, mensualidad y comisión. En celular los datos secundarios se pliegan para dejar visibles primero los que cambian decisiones."
                bullets={[
                  "Comisión válida significa que hoy cumple las condiciones para generar estimación.",
                  "Registrado incluye comisiones históricas confirmadas aunque el proyecto cambie de estado después.",
                ]}
              />
            </Stack>
            <Typography variant="body2" sx={{ mt: 0.3, color: BRAND.muted }}>
              Revisá estado, mensualidad y comisión; abrí los detalles sólo cuando los necesites.
            </Typography>
          </Box>

          <TableContainer
            sx={{
              borderRadius: { md: 3 },
              border: { xs: "none", md: `1px solid ${BRAND.border}` },
              overflow: { xs: "visible", md: "auto" },
            }}
          >
            <ResponsiveOperationalTable
              sx={{ minWidth: 980 }}
              collapseAfter={2}
              mobileLabel="Proyectos de tu red"
            >
              <TableHead>
                <TableRow sx={{ bgcolor: BRAND.page }}>
                  <TableCell>Proyecto y cliente</TableCell>
                  <TableCell>Relación</TableCell>
                  <TableCell>Módulos contratados</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Mensualidad</TableCell>
                  <TableCell align="right">Tu comisión</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((row) => {
                  const status = billingStatus(row.billing?.status);
                  const commission = commissionStatus(row, program.enabled !== false);
                  const accrued = Number(row.commission?.available || 0) + Number(row.commission?.paid || 0);
                  const isDirect = row.relationship === "direct";

                  return (
                    <TableRow key={row.project?.id} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                          {row.project?.name || "Proyecto"}
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", color: BRAND.muted }}>
                          {row.owner?.name || "Cliente"} · {row.owner?.email || "Sin email"}
                        </Typography>
                        {row.branch ? (
                          <Typography variant="caption" sx={{ display: "block", mt: 0.25, color: BRAND.muted }}>
                            Rama: {row.branch.name || row.branch.email}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={isDirect ? "primary" : "secondary"}
                          label={isDirect ? "Directo" : "Red ampliada"}
                          sx={{ fontWeight: 900 }}
                        />
                        {!isDirect ? (
                          <Typography variant="caption" sx={{ display: "block", mt: 0.45, color: BRAND.muted }}>
                            {row.relationshipDepth} vínculos desde vos
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 330 }}>
                        <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                          {(row.billing?.services || []).length ? (
                            row.billing.services.map((service) => (
                              <Chip key={service} size="small" label={service} variant="outlined" />
                            ))
                          ) : (
                            <Typography variant="body2" sx={{ color: BRAND.muted }}>
                              {row.billing?.planCode || "Sin módulos definidos"}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.45} alignItems="flex-start">
                          <Chip size="small" label={status.label} color={status.color} sx={{ fontWeight: 850 }} />
                          <Typography variant="caption" sx={{ color: BRAND.muted }}>
                            Próximo: {dateLabel(row.billing?.nextPaymentDate)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                          {money(row.billing?.monthlyAmount, row.billing?.currency)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: BRAND.muted }}>
                          {row.billing?.paymentMode === "recurring" ? "Recurrente" : row.billing?.paymentMode === "one_time" ? "Por período" : "Sin débito"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack spacing={0.45} alignItems={{ xs: "flex-start", md: "flex-end" }}>
                          <Typography sx={{ fontWeight: 950, color: row.commission?.eligible ? "success.main" : BRAND.muted }}>
                            {money(row.commission?.estimatedMonthly, row.billing?.currency)} / mes
                          </Typography>
                          <Chip
                            size="small"
                            color={commission.color}
                            variant={row.commission?.eligible ? "filled" : "outlined"}
                            label={commission.label}
                            sx={{ maxWidth: "100%", fontWeight: 850, "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }}
                          />
                          <Typography variant="caption" sx={{ display: "block", color: BRAND.muted }}>
                            {percent(row.commission?.percentage)}% · registrado {money(accrued, row.billing?.currency)}
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!projects.length && !loading ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 4, textAlign: "center" }}>
                      <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                        Todavía no hay proyectos asociados
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.4, color: BRAND.muted }}>
                        Compartí tu enlace para empezar a formar tu red.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 4, textAlign: "center" }}>
                      <CircularProgress size={26} />
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </ResponsiveOperationalTable>
          </TableContainer>
        </Stack>
      </CardContent>
    </Card>
  );
}
